import React, { ReactElement, useEffect, useState, useCallback, useRef } from 'react'
import { navigate } from 'gatsby'
import { IFaq } from './types'
import RichText from '../RichText'
import { useLocation } from '@reach/router'
import { isWithinOpeningHours } from '../utils/isWithinOpeningHours'
import { IS_BROWSER } from '../../utils/constants'
import trackingEvent from '../../utils/trackingEvent'
import { BLOCKS, RenderNode } from '../RichText/types'

// MUI
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Typography from '@mui/material/Typography'
import Grid from '@mui/material/Grid'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import ParrotfishIcon from '../ParrotfishIcon'

const Faq = ({
  faqItems,
  displayTitle,
  displayShowMore,
  variant,
  firstOpen,
  deeplinking = true,
}: IFaq): ReactElement => {
  const styles = {
    root: {
      '& .MuiAccordion-root:not(.Mui-expanded)': {
        marginBottom: '4px',
      },
      '& .MuiAccordionDetails-root': {
        display: 'block',
        '& .MuiLink-root': {
          fontSize: 'inherit',
        },
      },
    },
    faqHeading: {
      textAlign: 'center',
      padding: '0 0 40px 0',
    },
    linkWrapper: {
      width: '100%',
      display: 'flex',
      justifyContent: 'center',
      padding: '21px 0 21px 0',
    },
    h4: {
      wordBreak: 'break-word',
      marginBottom: '1em',
    },
  }

  const numbersToDisplay = 5

  const renderNode: Partial<RenderNode> = {
    [BLOCKS.PARAGRAPH]: (node, children) => {
      const element = (
        <Typography variant={'body2'} paragraph>
          {children}
        </Typography>
      )
      return element
    },

    [BLOCKS.HEADING_4]: (node, children) => {
      const element = (
        <Typography sx={styles.h4} component="h4" variant={'subtitle2'}>
          {children}
        </Typography>
      )
      return element
    },
  }
  const { hash: deepLinkId, pathname } = useLocation()

  const ref = useRef<HTMLDivElement>()

  const [expandedItem, setExpandedItem] = useState<number>(firstOpen ? 0 : -1)

  const showFaqItemDisplayMode = (mode?: 'Default' | 'Show' | 'Hide') => {
    const showByDefault = mode === null || mode === 'Default'
    if (isWithinOpeningHours()) {
      return showByDefault || mode === 'Show'
    } else {
      return showByDefault || mode === 'Hide'
    }
  }

  const accordionActiveIndex = faqItems?.findIndex(faqItem => {
    const { id } = faqItem
    return `#${id}` === deepLinkId
  })
  const loadMoreFromState = IS_BROWSER && window.history.state?.loadedMore
  const [loadedMore, setLoadedMore] = useState(loadMoreFromState ? loadMoreFromState : expandedItem > numbersToDisplay)
  const faqtoload = loadedMore ? faqItems : faqItems && faqItems?.slice(0, numbersToDisplay)
  const [slicedFaqItems, setSlicedFaqItems] = useState(faqtoload)

  const getAccordion = () => {
    if (firstOpen === true) {
      firstOpen = false
    }
  }

  useEffect(() => {
    if (!IS_BROWSER) return
    const items = ref?.current?.querySelectorAll<HTMLDivElement>('div[data-element-type*="faq"]')
    if (items) {
      Array.from(items).forEach((item: HTMLDivElement, i) => {
        if (!faqItems[i].showhideDuringOpeningHours) return
        const isDisplayed = showFaqItemDisplayMode(faqItems[i]?.showhideDuringOpeningHours)
        if (!isDisplayed) {
          item.style.display = 'none'
        }
      })
    }
  }, [isWithinOpeningHours])

  useEffect(() => {
    if (accordionActiveIndex === null) return
    if (accordionActiveIndex !== -1) {
      const { id } = faqItems[accordionActiveIndex]
      setExpandedItem(accordionActiveIndex)
      const accordionElement = document.querySelector(`[data-scroll-id*="${id}"]`)
      if (accordionElement) {
        if (accordionActiveIndex !== 0) {
          getAccordion()
        }
      } else {
        loadMoreClick()
        setTimeout(() => {
          getAccordion()
        }, 1)
      }
    }
  }, [])

  const expandClick = useCallback(
    (faqIndex: number) => {
      if (faqIndex === expandedItem) {
        setExpandedItem(-1)
        deeplinking && navigate(window.location.pathname)
      } else {
        const newUrl = `${window.location.pathname}#${faqItems[faqIndex].id}`
        setExpandedItem(faqIndex)
        deeplinking && navigate(newUrl)

        // Accordion tracking
        const accordion = {
          name: faqItems[faqIndex].summary,
          position: faqIndex + 1,
        }
        trackingEvent({ event: 'accordion_open', accordion })
      }
    },
    [expandedItem, setExpandedItem, loadedMore, setLoadedMore],
  )

  const loadMoreClick = useCallback(() => {
    setLoadedMore(!loadedMore)
    if (loadedMore) {
      setSlicedFaqItems(faqItems?.slice(0, numbersToDisplay))
    } else {
      setSlicedFaqItems(faqItems)
    }
  }, [loadedMore, setLoadedMore, setSlicedFaqItems, faqItems])

  return (
    <Box sx={styles.root} ref={ref}>
      {displayTitle && (
        <Typography sx={styles.faqHeading} variant={'h4'} component={'h2'}>
          {displayTitle}
        </Typography>
      )}
      {slicedFaqItems &&
        slicedFaqItems?.map(({ summary, summaryIcon, details, id }, i) => {
          return (
            <Accordion
              onChange={() => expandClick(i)}
              key={id}
              expanded={expandedItem === i}
              data-scroll-id={id}
              data-element-type="faq"
              {...(pathname && pathname.includes('/kundeservice') ? { variant: 'outlined' } : { variant: variant })}
            >
              <AccordionSummary id={`FAQ-summary-${id}`} aria-controls={`FAQ-details-${id}`}>
                {summaryIcon && <ParrotfishIcon {...summaryIcon} />}
                <Typography variant={'subtitle2'} component={'h3'}>
                  {summary}
                </Typography>
              </AccordionSummary>
              <AccordionDetails id={`FAQ-details-${id}`} aria-labelledby={`${id}-summary`}>
                <RichText
                  renderNode={renderNode}
                  raw={details?.raw ? details?.raw : (details as string)}
                  references={details?.references}
                />
              </AccordionDetails>
            </Accordion>
          )
        })}
      {displayShowMore && faqItems.length > numbersToDisplay && (
        <Grid sx={styles.linkWrapper}>
          <Button onClick={() => loadMoreClick()} variant="text" id="FAQ-show-more" data-testid="FAQ-show-more">
            {loadedMore ? 'Vis færre' : `Vis alle (${faqItems.length})`}
          </Button>
        </Grid>
      )}
    </Box>
  )
}

export default Faq
